// Creator-Mocks — aus Scrolly Platform.html, Design-Quelle.
//
// Phase 11 Step 3: Creators leben in `lib/mock/` und werden in `scrollys.ts`
// denormalisiert eingebettet (das Scrolly-Shape hat `creator: {handle, name, avatar}`
// direkt, ohne Join). Dieser Export bleibt trotzdem als Single-Source-of-Truth
// für Creator-Profilseiten, Follow-Buttons etc.
//
// `avatar` ist aktuell ein Farb-Token (Hex) — der Design-HTML rendert Avatare als
// farbige Blobs mit Initialen. Bei Migration auf echte Avatare wird das Feld zur URL.
//
// Phase 11.5 (2026-04-22): `type` + `externalHomepage` für externe Publisher.
// Siehe handoff/FEATURE-11.5-THIRD-PARTY-PREP.md. Native-Creators sind alles,
// was vor dieser Amendment existiert. Externe Creators (Pudding, Bloomberg,
// SZ-Interaktiv) sind Placeholder für Phase-12-Publisher-Onboarding — kein
// Login, kein Studio-Zugriff, ihre Scrollys werden per `source: "embed"`
// verlinkt.

export type MockCreator = {
  handle: string;
  name: string;
  avatar: string; // Farb-Token oder später URL
  followers: string;
  followerCount: number;
  scrollys: number;
  verified: boolean;
  /** Kurze Bio für den Creator-Header, max ~200 Zeichen. */
  bio: string;
  /** Längere Bio für den About-Tab — optional, mehrzeilig ok. */
  bioLong?: string;
  /** Website-URL, auf About-Tab verlinkt. Absolut oder intern. */
  website?: string;
  /**
   * Social-Handles — nur der Username, nicht die volle URL. Die Links
   * werden in About-Tab aufgelöst (z.B. `twitter.com/{handle}`).
   */
  social?: {
    twitter?: string;
    instagram?: string;
    mastodon?: string;
  };
  /**
   * Publisher-Typ — siehe handoff/FEATURE-11.5-THIRD-PARTY-PREP.md.
   * - `native`  = Scrolly-Creator mit Account im Studio.
   * - `external` = externer Publisher (Pudding, Bloomberg, …), kuratiert
   *                angelegt, kein Login. Profile sind read-only.
   */
  type: "native" | "external";
  /** Nur wenn `type === "external"`: Homepage des Publishers, absolut. */
  externalHomepage?: string;
};

export const creators: MockCreator[] = [
  {
    handle: "datastories",
    name: "Data Stories",
    avatar: "#FF3366",
    followers: "218K",
    followerCount: 218_000,
    scrollys: 42,
    verified: true,
    bio: "Eine wöchentliche scrollbare Datengeschichte.",
    bioLong:
      "Wir übersetzen Zahlen in Geschichten, an die man sich erinnert. Jeden Freitag eine neue Datenvisualisierung — scrollbar, sauber recherchiert, ohne Klick-Bait.",
    website: "https://datastories.example",
    social: { twitter: "datastories", mastodon: "datastories@chaos.social" },
    type: "native",
  },
  {
    handle: "atlas",
    name: "Atlas",
    avatar: "#06B6D4",
    followers: "94K",
    followerCount: 94_000,
    scrollys: 28,
    verified: true,
    bio: "Orte, erzählt von ihrer Landschaft.",
    bioLong:
      "Geografie als Erzählung. Wir nutzen 3D-Terrain, Satellitendaten und Höhenprofile, um Landschaften begehbar zu machen — ohne sie zu betreten.",
    website: "https://atlas.example",
    social: { instagram: "atlas.scrollys" },
    type: "native",
  },
  {
    handle: "theledger",
    name: "The Ledger",
    avatar: "#10B981",
    followers: "156K",
    followerCount: 156_000,
    scrollys: 61,
    verified: false,
    bio: "Finanzen ohne Fachjargon.",
    type: "native",
  },
  {
    handle: "codex",
    name: "Codex",
    avatar: "#D97706",
    followers: "72K",
    followerCount: 72_000,
    scrollys: 19,
    verified: false,
    bio: "Alte Geschichte, neues Scrollen.",
    type: "native",
  },
  {
    handle: "framerweekly",
    name: "Framer Weekly",
    avatar: "#3B82F6",
    followers: "340K",
    followerCount: 340_000,
    scrollys: 87,
    verified: true,
    bio: "Der Design-Newsletter, den du wirklich liest.",
    bioLong:
      "Wir verwandeln die wichtigsten Design-Diskurse der Woche in scrollbare Essays: Typo, Interaction, Brand, Product. Kuratiert von einem kleinen Team in Berlin.",
    website: "https://framerweekly.example",
    social: { twitter: "framerweekly", instagram: "framerweekly" },
    type: "native",
  },
  {
    handle: "orbital",
    name: "Orbital",
    avatar: "#6366F1",
    followers: "512K",
    followerCount: 512_000,
    scrollys: 34,
    verified: true,
    bio: "Weltraum, maßstabsgetreu.",
    type: "native",
  },
  {
    handle: "feldnotiz",
    name: "Feldnotiz",
    avatar: "#8B5CF6",
    followers: "48K",
    followerCount: 48_000,
    scrollys: 23,
    verified: false,
    bio: "Reportagen, die scrollen wollen.",
    type: "native",
  },
  {
    handle: "studio-kranz",
    name: "Studio Kranz",
    avatar: "#EC4899",
    followers: "112K",
    followerCount: 112_000,
    scrollys: 31,
    verified: true,
    bio: "Visuelle Essays über Marken und Räume.",
    type: "native",
  },

  // ---- Externe Publisher (Phase 11.5) -----------------------------------
  // Placeholder-Creators für externe Scrollys via `source: "embed"`.
  // Kein Login, kein Studio — die Profil-Seite ist kuratiert.
  // Follower-Zahlen sind plausibel geraten (redaktionelle Mocks).

  {
    handle: "pudding",
    name: "The Pudding",
    avatar: "#F59E0B",
    followers: "1.2M",
    followerCount: 1_200_000,
    scrollys: 180,
    verified: true,
    bio: "Visual essays that explain the internet, with data.",
    bioLong:
      "The Pudding is a digital publication that explains ideas debated in culture with visual essays. Our stories are reported, researched, and engineered — nothing is off the table, as long as it is a good story.",
    website: "https://pudding.cool",
    social: { twitter: "puddingviz", instagram: "puddingvisual" },
    type: "external",
    externalHomepage: "https://pudding.cool",
  },
  {
    handle: "bloomberg",
    name: "Bloomberg Graphics",
    // Bloomberg-Brand-Orange — bleibt auf Editorial (hell) wie Immersive (dunkel)
    // sichtbar. Reines Schwarz (Bloomberg Terminal) wäre auf Immersive unsichtbar.
    avatar: "#F97316",
    followers: "890K",
    followerCount: 890_000,
    scrollys: 420,
    verified: true,
    bio: "Märkte, Geld und Macht — visuell erklärt.",
    type: "external",
    externalHomepage: "https://www.bloomberg.com/graphics",
  },
  {
    handle: "sz-interaktiv",
    name: "SZ Interaktiv",
    avatar: "#002E5F",
    followers: "312K",
    followerCount: 312_000,
    scrollys: 145,
    verified: true,
    bio: "Das Datenjournalismus-Team der Süddeutschen Zeitung.",
    type: "external",
    externalHomepage: "https://www.sueddeutsche.de/projekte",
  },
];

export const CREATORS_BY_HANDLE = Object.fromEntries(
  creators.map((c) => [c.handle, c]),
) as Record<string, MockCreator>;
